import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { FadeUp } from "@/components/Reveal";
import { REGIONS, FILIERES_META } from "@/data/regionalMap";

const pick = (obj, locale) => (obj ? obj[locale] || obj.fr : "");

/**
 * RegionalMap — Cartographie interactive des 9 régions de la Guinée-Bissau
 * reliées à leurs filières porteuses. Trilingue.
 */
export default function RegionalMap() {
  const { locale } = useLanguage();
  const [selectedFiliere, setSelectedFiliere] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState("cacheu");

  const region = REGIONS.find((r) => r.slug === selectedRegion) || REGIONS[0];
  const L = (fr, en, pt) => (locale === "en" ? en : locale === "pt" ? pt : fr);

  const regionMatches = (r) =>
    !selectedFiliere || r.filieres.includes(selectedFiliere);

  return (
    <section
      data-testid="regional-map"
      className="border-t border-nanda-line bg-nanda-ink text-white overflow-hidden"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28">
        <FadeUp>
          <div className="label-overline text-nanda-gold mb-8" style={{ color: "#D4A24E" }}>
            {L("Cartographie des opportunités", "Opportunities map", "Cartografia das oportunidades")}
          </div>
          <div className="grid md:grid-cols-12 gap-12 items-end mb-12">
            <h2 className="md:col-span-7 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-white">
              {L(
                "Chaque filière a sa géographie.",
                "Every value chain has its geography.",
                "Cada fileira tem a sua geografia."
              )}
            </h2>
            <p className="md:col-span-5 text-white/80 text-[16px] md:text-[17px] leading-[1.75]">
              {L(
                "9 régions administratives, 7 filières porteuses. Cliquez sur une région pour découvrir ses opportunités, ou filtrez par filière pour voir où elle se déploie.",
                "9 administrative regions, 7 value chains. Click a region to see its opportunities, or filter by value chain to see where it deploys.",
                "9 regiões administrativas, 7 fileiras porteiras. Clique numa região para descobrir as suas oportunidades, ou filtre por fileira para ver onde se desenvolve."
              )}
            </p>
          </div>
        </FadeUp>

        {/* Filtres filières */}
        <div className="flex flex-wrap gap-2 mb-10" data-testid="regional-map-filters">
          <button
            data-testid="filter-all"
            onClick={() => setSelectedFiliere(null)}
            className={`label-overline px-4 py-2 border transition-colors duration-300 ${
              !selectedFiliere
                ? "bg-white text-nanda-ink border-white"
                : "bg-transparent text-white/70 border-white/30 hover:border-white"
            }`}
          >
            {L("Toutes les filières", "All value chains", "Todas as fileiras")}
          </button>
          {Object.entries(FILIERES_META).map(([key, meta]) => (
            <button
              key={key}
              data-testid={`filter-${key}`}
              onClick={() => setSelectedFiliere(key === selectedFiliere ? null : key)}
              className={`label-overline px-4 py-2 border transition-colors duration-300 ${
                selectedFiliere === key
                  ? "text-nanda-ink border-transparent"
                  : "bg-transparent text-white/70 border-white/30 hover:border-white hover:text-white"
              }`}
              style={selectedFiliere === key ? { backgroundColor: meta.color, borderColor: meta.color } : {}}
            >
              {meta[locale] || meta.fr}
            </button>
          ))}
        </div>

        {/* Carte SVG + Détail région */}
        <div className="grid md:grid-cols-12 gap-8 md:gap-12">
          <div className="md:col-span-7">
            <div className="relative bg-white/[0.03] border border-white/10 p-8 md:p-10">
              <svg
                viewBox="0 0 100 100"
                className="w-full h-auto"
                data-testid="regional-map-svg"
              >
                {/* Océan Atlantique en fond */}
                <text x="2" y="6" fontSize="2.2" fill="rgba(255,255,255,0.35)" fontFamily="Montserrat" letterSpacing="0.2">
                  {L("Atlantique", "Atlantic", "Atlântico")}
                </text>
                <text x="88" y="98" fontSize="2.2" fill="rgba(255,255,255,0.35)" fontFamily="Montserrat" letterSpacing="0.2">
                  {L("Guinée", "Guinea", "Guiné")}
                </text>

                {REGIONS.map((r) => {
                  const isSelected = r.slug === selectedRegion;
                  const isMatch = regionMatches(r);
                  const fillColor =
                    isSelected
                      ? "#A66E4E"
                      : isMatch
                        ? selectedFiliere
                          ? FILIERES_META[selectedFiliere]?.color || "rgba(255,255,255,0.15)"
                          : "rgba(255,255,255,0.15)"
                        : "rgba(255,255,255,0.05)";
                  return (
                    <g key={r.slug}>
                      <path
                        d={r.d}
                        fill={fillColor}
                        stroke={isSelected ? "#fff" : "rgba(255,255,255,0.4)"}
                        strokeWidth={isSelected ? 0.6 : 0.3}
                        opacity={isMatch ? 1 : 0.35}
                        onClick={() => setSelectedRegion(r.slug)}
                        data-testid={`region-${r.slug}`}
                        style={{ cursor: "pointer", transition: "all 0.3s ease" }}
                      />
                      {/* Étiquette de la région */}
                      <text
                        x={getCentroid(r.d).x}
                        y={getCentroid(r.d).y}
                        fontSize="2.4"
                        fill={isSelected || isMatch ? "#fff" : "rgba(255,255,255,0.4)"}
                        textAnchor="middle"
                        fontFamily="Cormorant Garamond, serif"
                        fontStyle="italic"
                        pointerEvents="none"
                      >
                        {pick(r.name, locale)}
                      </text>
                    </g>
                  );
                })}
              </svg>
              <p className="text-white/50 text-xs mt-4 italic">
                {L(
                  "Représentation stylisée à des fins de lecture. Les frontières exactes suivent le découpage administratif national.",
                  "Stylised representation for readability. Actual borders follow the national administrative division.",
                  "Representação estilizada para efeitos de leitura. As fronteiras exactas seguem a divisão administrativa nacional."
                )}
              </p>
            </div>
          </div>

          {/* Panneau détail */}
          <div className="md:col-span-5 flex flex-col" data-testid="regional-map-details">
            <div className="border-l-2 pl-6" style={{ borderColor: "#A66E4E" }}>
              <div className="flex items-center gap-2 mb-4">
                <MapPin size={16} strokeWidth={1.5} style={{ color: "#D4A24E" }} />
                <span className="label-overline" style={{ color: "#D4A24E" }}>
                  {L("Région administrative", "Administrative region", "Região administrativa")}
                </span>
              </div>
              <h3 className="font-serif text-3xl md:text-4xl text-white mb-2 leading-tight">
                {pick(region.name, locale)}
              </h3>
              <div className="label-overline text-white/60 mb-6">
                {L("Chef-lieu :", "Capital:", "Capital:")} <span className="text-white">{region.capital}</span>
              </div>
              <p className="text-white/85 leading-relaxed mb-8">{pick(region.note, locale)}</p>

              <div className="label-overline text-white/60 mb-4">
                {L("Filières porteuses", "Value chains", "Fileiras porteiras")}
              </div>
              <div className="flex flex-wrap gap-2 mb-8">
                {region.filieres.map((f) => {
                  const meta = FILIERES_META[f];
                  return (
                    <Link
                      key={f}
                      to={f === "peche" ? "/secteurs/peche" : `/secteurs/${f}`}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-xs tracking-widest uppercase border border-white/25 text-white hover:border-white hover:bg-white/10 transition-colors"
                      style={{ borderLeftWidth: 2, borderLeftColor: meta.color }}
                      data-testid={`region-filiere-${f}`}
                    >
                      {meta[locale] || meta.fr}
                    </Link>
                  );
                })}
              </div>

              <Link
                to="/opportunites"
                className="inline-flex items-center gap-2 text-white/80 hover:text-white label-overline border-b border-white/40 pb-1"
              >
                {L("Voir tout le panorama", "See the full panorama", "Ver todo o panorama")}
                <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* Calcul rudimentaire du centroïde d'un path SVG rectilinéaire. */
function getCentroid(d) {
  const nums = d.match(/-?\d+(\.\d+)?/g).map(Number);
  const xs = nums.filter((_, i) => i % 2 === 0);
  const ys = nums.filter((_, i) => i % 2 === 1);
  const x = xs.reduce((a, b) => a + b, 0) / xs.length;
  const y = ys.reduce((a, b) => a + b, 0) / ys.length;
  return { x, y };
}
